import { Button, Group, SegmentedControl, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useReclaimAuthorship } from '@/module/Author/Publication/model/PublicationQuery';
import { ReclaimRequest, ReclaimablePublication } from '@/module/Author/Publication/model/PublicationTypes';
import { ImportCard } from '@/module/Sync/components/ImportCard';
import { ImportReclaimModal } from '@/module/Sync/components/ImportReclaimModal';
import { SyncModal } from '@/module/Sync/components/SyncModal';
import { ImportEntityType, ImportItem, ImportStateType, importEntityTypeList } from '@/module/Sync/model/SyncTypes';

import { useAppNotification } from '@feature/utils';

interface SyncItemProps {
  item: ImportItem;
  state: ImportStateType;
  publication: ReclaimablePublication | null;
  next: () => void;
  onAdd: () => void;
  sourceType: string;
}

export function SyncItem({ item, state, next, sourceType, publication }: SyncItemProps) {
  const [selectedImportType, setImportType] = useState<ImportEntityType>(item.type ?? 'ARTICLE');
  const [opened, { open, close }] = useDisclosure(false);
  const [openedReclaimed, { open: openReclaimed, close: closeReclaimed }] = useDisclosure(false);
  const { t } = useTranslation();
  const onSuccess = () => {
    close();
    next();
  };

  const notification = useAppNotification();
  const reclaimMutation = useReclaimAuthorship();
  const onReclaimAuthorship = (request: ReclaimRequest) => {
    reclaimMutation.mutate(request, {
      onError: notification.error,
      onSuccess: () => {
        notification.success();
        next();
      },
    });
  };

  return (
    <Stack>
      <ImportCard item={item} />
      {opened && (
        <SyncModal
          key={`${selectedImportType}-${item.title}`}
          item={item}
          type={selectedImportType}
          close={close}
          onSuccess={onSuccess}
          sourceType={sourceType}
        />
      )}
      {openedReclaimed && publication && (
        <ImportReclaimModal
          close={closeReclaimed}
          publication={publication}
          onReclaimAuthorship={onReclaimAuthorship}
        />
      )}
      <div>
        {state === 'IMPORTED_BUT_NOT_CLAIMED' && <Text c="dimmed">{t('sync.notClaimedState')}</Text>}
        {state === 'NOT_IMPORTED' && <Text c="dimmed">{t('sync.notImportedState')}</Text>}
      </div>
      <SegmentedControl
        value={selectedImportType}
        onChange={value => {
          setImportType(value as ImportEntityType);
        }}
        data={importEntityTypeList.map(ie => ({ label: t(`sync.entityType.${ie}`), value: ie }))}
      />
      <Group
        mt="10px"
        justify="center"
      >
        <Button
          variant="outline"
          onClick={next}
        >
          {t('button.ignore')}
        </Button>
        {state === 'NOT_IMPORTED' && <Button onClick={open}>{t('button.add')}</Button>}
        {state === 'IMPORTED_BUT_NOT_CLAIMED' && <Button onClick={openReclaimed}>{t('sync.addAsAuthor')}</Button>}
      </Group>
    </Stack>
  );
}
